/**
 * Ballot text that arrives as HTML, as readable plain text with its paragraphs.
 *
 * Democracy Works ships a measure's full text as markup — New York's 2026
 * Question 1 is three `<p>` elements with empty `<p></p>` spacers between them.
 * Reading it through `textContent` (web) or a bare tag strip (mobile) joined the
 * paragraphs with nothing between them: "…Use Public SpaceSimplify process…",
 * "…Council input.'Yes' speeds up…" (staging QA, September 2026). Block
 * elements now become paragraph breaks, `<br>` a line break, and inline tags
 * nothing at all; render the result with `white-space: pre-line` on web (a
 * React Native `Text` keeps newlines as they are).
 *
 * The backend PDF converts the same way (`utils/html_text.py`); the tests share
 * their cases. Identical in both clients (`src/utils/htmlText.ts`).
 */

const BLOCK_TAG_NAMES = [
  'p',
  'div',
  'ul',
  'ol',
  'h[1-6]',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'section',
  'article',
  'blockquote',
  'header',
  'footer',
  'pre',
  'dl',
  'dt',
  'dd',
  'figure',
  'figcaption',
  'address',
  'hr',
];
const BLOCK_TAG = new RegExp(`</?(?:${BLOCK_TAG_NAMES.join('|')})\\b[^>]*>`, 'gi');
// Each list item starts its own line; the closing tag adds nothing.
const LIST_ITEM = /<li\b[^>]*>/gi;
const LINE_BREAK = /<br\b[^>]*>/gi;
const ANY_TAG = /<[^>]*>/g;
// Markup, rather than a stray "<" in prose ("turnout < 50%").
const HAS_TAG = /<(?:[a-zA-Z][a-zA-Z0-9]*|\/[a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/;
const INLINE_SPACE = /[ \t\f\v ]+/g;

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  ndash: '–',
  mdash: '—',
  lsquo: '‘',
  rsquo: '’',
  ldquo: '“',
  rdquo: '”',
  hellip: '…',
  bull: '•',
  middot: '·',
  sect: '§',
  para: '¶',
  copy: '©',
  reg: '®',
  trade: '™',
  deg: '°',
  cent: '¢',
  pound: '£',
  euro: '€',
  frac12: '½',
  frac14: '¼',
  frac34: '¾',
  times: '×',
  divide: '÷',
  aacute: 'á',
  eacute: 'é',
  iacute: 'í',
  oacute: 'ó',
  uacute: 'ú',
  ntilde: 'ñ',
  uuml: 'ü',
  Aacute: 'Á',
  Eacute: 'É',
  Iacute: 'Í',
  Oacute: 'Ó',
  Uacute: 'Ú',
  Ntilde: 'Ñ',
};

function decodeEntities(text: string): string {
  return text.replace(/&(#\d+|#x[0-9a-f]+|[a-z][a-z0-9]*);/gi, (whole, entity: string) => {
    if (entity[0] === '#') {
      const code =
        entity[1] === 'x' || entity[1] === 'X'
          ? parseInt(entity.slice(2), 16)
          : parseInt(entity.slice(1), 10);
      return Number.isFinite(code) && code > 0 && code <= 0x10ffff
        ? String.fromCodePoint(code)
        : whole;
    }
    return NAMED_ENTITIES[entity] ?? NAMED_ENTITIES[entity.toLowerCase()] ?? whole;
  });
}

/**
 * Plain text of `value`, keeping its paragraph and line breaks. Text with no
 * markup keeps its own line breaks; in markup, source whitespace — newlines
 * included — is insignificant, as in a browser.
 */
export function htmlToText(value: string | null | undefined): string {
  const raw = value ?? '';
  if (!raw.trim()) return '';
  let text: string;
  if (HAS_TAG.test(raw)) {
    text = raw
      .replace(/\s+/g, ' ')
      .replace(LINE_BREAK, '\n')
      .replace(LIST_ITEM, '\n')
      .replace(BLOCK_TAG, '\n\n')
      .replace(ANY_TAG, '');
  } else {
    text = raw.replace(/\r\n?/g, '\n');
  }
  return decodeEntities(text)
    .split('\n')
    .map((line) => line.replace(INLINE_SPACE, ' ').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
